import { postDraftClaim, putDraftClaim } from '../../web-apis-client';
import {
  generateDraftAnswersFromForm,
  removeBlankOrNullProperties,
} from '../utils';
import { DraftClaim } from '../../../data-models';
import { IFormOptions } from '../../../kontent-models';
import Handlebars from 'handlebars';
import * as hbh from '../../../hooks/use-handlebars/helpers';
import { log } from '../../log';

const getTemplateLineNumber = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error) || '';
  const lineMatch = message.match(/line\s+(\d+)/i);
  if (!lineMatch) {
    return undefined;
  }

  const parsed = Number(lineMatch[1]);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getTemplateContextSnippet = (template: string, lineNumber?: number) => {
  if (!lineNumber) {
    return '';
  }

  const lines = template.split(/\r?\n/);
  if (lineNumber < 1 || lineNumber > lines.length) {
    return '';
  }

  const start = Math.max(1, lineNumber - 2);
  const end = Math.min(lines.length, lineNumber + 2);
  const context = lines
    .slice(start - 1, end)
    .map((line, idx) => `${start + idx}: ${line}`)
    .join('\n');

  return `\nTemplate context:\n${context}`;
};

export const getFieldValues = (
  fvg: (key: string) => any,
  fieldList?: string[],
) => {
  // return object
  const r: any = {};

  fieldList?.forEach((fieldName) => {
    // field value getter result
    let fvgr = fvg(fieldName);

    if (typeof fvgr === 'string') {
      // just handle newlines for now, tried JSON.stringify()
      // but other issues arise with that approach
      fvgr = fvgr.replace(/\n/g, '\\n');
    }

    r[fieldName] = fvgr;
  });

  return r;
};

export const generatePlan = async (
  formValueGetter: (id: string) => any,
  formOptionsTemplate: string,
  leaveLogicPlanId?: string,
) => {
  const initialOptions: IFormOptions = JSON.parse(formOptionsTemplate);
  log.debug({ initialOptions });

  // get the other field values based on the draftFields list
  hbh.registerSelf(Handlebars, formValueGetter);
  const formResult = getFieldValues(
    formValueGetter,
    initialOptions.draftFields,
  );
  log.debug({ formResult });

  // activate the template
  let activatedOptions = '';
  try {
    activatedOptions = Handlebars.compile(formOptionsTemplate)(formResult);
  } catch (error) {
    const lineNumber = getTemplateLineNumber(error);
    const contextSnippet = getTemplateContextSnippet(
      formOptionsTemplate,
      lineNumber,
    );
    const message =
      error instanceof Error ? error.message : String(error) || 'Unknown error';

    log.error(
      `Handlebars template compile failed${
        lineNumber ? ` at line ${lineNumber}` : ''
      }: ${message}${contextSnippet}`,
    );

    throw new Error(
      `Invalid form options Handlebars template${
        lineNumber ? ` at line ${lineNumber}` : ''
      }. ${message}`,
    );
  }
  log.debug({ activatedOptions });

  // build an answer object based on the formResult and the options.draftAnswerMap
  let finalOptions: IFormOptions;
  try {
    finalOptions = JSON.parse(activatedOptions);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : String(error) || 'Unknown error';
    log.error(`Activated options JSON parse failed: ${message}`);
    throw new Error(`Activated form options JSON is invalid. ${message}`);
  }
  log.debug({ finalOptions });

  const finalAnswers = removeBlankOrNullProperties(
    finalOptions.draftAnswerMap as any,
  );

  // convert to array of answers
  const dcAnswersArray = generateDraftAnswersFromForm(
    finalAnswers,
    finalOptions.draftQuestionMap as any,
  );
  log.info({ dcAnswersArray });

  const finalDraftObject: DraftClaim = {
    answers: dcAnswersArray,
    mode: 'reset',
    leaveLogicPlanId: leaveLogicPlanId || 'No Id present',
  };

  // await postDraftClaim(answers)
  const dcResult = await postDraftClaim(finalDraftObject);
  log.info({ dcResult });

  const draftClaim: DraftClaim = (await putDraftClaim()) as DraftClaim;
  log.info({ draftClaim });

  return { draftClaim };
};
